import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { X, Zap, MessageCircle, ArrowRight } from "lucide-react";
import { waLink } from "@/lib/constants";

const STORAGE_KEY = "swala_express_popup_dismissed_v1";
const DELAY_MS = 4500;
const SUPPRESS_PATHS = ["/express-delhi-ncr"];

export default function ExpressPopup() {
  const [show, setShow] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (SUPPRESS_PATHS.includes(location.pathname)) return;
    if (sessionStorage.getItem(STORAGE_KEY) === "1") return;
    const t = setTimeout(() => setShow(true), DELAY_MS);
    return () => clearTimeout(t);
  }, [location.pathname]);

  const close = () => {
    setShow(false);
    try { sessionStorage.setItem(STORAGE_KEY, "1"); } catch {}
  };

  if (!show) return null;

  return (
    <div
      data-testid="express-popup"
      className="fixed inset-0 z-[60] flex items-center justify-center px-4 py-6 overflow-y-auto"
    >
      {/* backdrop */}
      <div
        onClick={close}
        className="absolute inset-0 bg-[#0E0709]/85 backdrop-blur-sm animate-[fadeIn_0.4s_ease]"
      />

      {/* modal */}
      <div
        data-testid="express-popup-modal"
        className="relative max-w-2xl w-full bg-[#1F1218] border border-[#3A2530] overflow-hidden shadow-2xl my-auto max-h-[calc(100vh-3rem)] overflow-y-auto"
        style={{ animation: "popIn 0.6s cubic-bezier(.16,1,.3,1)" }}
      >
        <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#F0C4BC] to-transparent" />
        <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-[#F0C4BC]/10 blur-3xl" />

        <button
          onClick={close}
          data-testid="express-popup-close"
          aria-label="Close"
          className="absolute top-4 right-4 z-10 w-10 h-10 border border-[#3A2530] flex items-center justify-center text-[#C4A8B0] hover:text-[#F8EDE7] hover:border-[#F0C4BC] transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="relative p-7 sm:p-9 md:p-14">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#F0C4BC] text-[#0E0709] text-[10px] tracking-[0.22em] uppercase font-medium mb-6">
            <Zap className="w-3.5 h-3.5" strokeWidth={2.4} />
            Delhi NCR Express
          </div>

          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-medium leading-[0.95] tracking-tight text-[#F8EDE7]">
            Need it<br/>
            <span className="italic-serif text-[#F0C4BC]">in 24 hours?</span>
          </h2>

          <p className="mt-5 text-base md:text-lg text-[#E8D5DA] font-normal leading-relaxed max-w-md">
            Last-minute wedding? Surprise corporate launch? Same-day pickup across Delhi NCR, next-day doorstep delivery. Premium quality, zero compromise.
          </p>

          <ul className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-lg">
            {[
              "Same-day pickup in Delhi NCR",
              "Doorstep delivery in 24 hours",
              "Free design proof in 2 hours",
              "WhatsApp updates throughout",
            ].map((it) => (
              <li key={it} className="flex items-center gap-3 text-sm text-[#F8EDE7]">
                <span className="w-1.5 h-1.5 bg-[#F0C4BC]" />
                {it}
              </li>
            ))}
          </ul>

          <div className="mt-9 flex flex-wrap gap-3">
            <a
              href={waLink("Hi! I need a Delhi NCR 24-hour express order. Please share details.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="express-popup-wa"
              onClick={close}
              className="bg-[#25D366] text-white px-7 py-4 hover:opacity-90 transition-opacity text-[12px] tracking-[0.22em] uppercase inline-flex items-center gap-3"
            >
              <MessageCircle className="w-4 h-4" />
              WhatsApp Now
            </a>
            <button
              onClick={close}
              data-testid="express-popup-dismiss"
              className="btn-outline-luxe border border-[#F0C4BC] text-[#F0C4BC] px-7 py-4 text-[12px] tracking-[0.22em] uppercase inline-flex items-center gap-3 hover:text-[#0E0709]"
            >
              Maybe Later <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          <p className="mt-6 text-[10px] tracking-[0.18em] uppercase text-[#C4A8B0]">
            ✦ Delhi · Gurgaon · Noida · Ghaziabad · Faridabad
          </p>

          <div className="mt-5 pt-5 border-t border-[#3A2530] flex flex-wrap items-center gap-x-5 gap-y-2">
            <span className="text-[9px] tracking-[0.28em] uppercase text-[#C4A8B0]">Featured On</span>
            <span className="inline-flex items-baseline gap-1">
              <span className="font-heading text-base text-[#F8EDE7]">Wedding</span>
              <span className="font-heading italic-serif text-base text-[#F0C4BC]">Sutra</span>
            </span>
            <span className="text-[#3A2530]">·</span>
            <span className="inline-flex items-baseline gap-1.5">
              <span className="text-[9px] tracking-[0.3em] uppercase text-[#C4A8B0]">Wedding</span>
              <span className="font-heading italic-serif text-base text-[#F8EDE7]">Bazaar</span>
            </span>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes popIn {
          from { opacity: 0; transform: translateY(20px) scale(0.96); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>
  );
}
